import React, { useState, useMemo, useRef } from "react";
import { Check, Loader2, AlertCircle, RefreshCw, Pencil } from "lucide-react";
import FoodItemActionsModal, { NutritionSummaryCard } from "./FoodItemActionsModal";
import { parseIntakeTimeForDisplay } from "../../../core/diet-plan/intakeTime";
import { formatCompactNutrition } from "../../../core/nutrients/calculateMealNutrients";

const FoodItemCard = ({
  food,
  updateFoodItem,
  retrySync,
  setAlertMessage: _setAlertMessage,
  setAlertType: _setAlertType,
  onDuplicate: _onDuplicate,
  isSelected = false,
  onSelect,
  showCheckbox = false,
  readOnly = false, // When true, card is display-only (no actions modal)
  intakeTimeReadOnly = false, // Timing is the meal-level intake window
}) => {
  const [showActionsModal, setShowActionsModal] = useState(false);
  const cardRef = useRef(null);

  const quantity = food.quantity || 1;
  const unit = food.quantity_type;
  const timeValue = parseIntakeTimeForDisplay(food.intake_time) || food.time || "";

  // Memoize available units to prevent recalculation
  const availableUnits = useMemo(() => {
    return food.food_details?.measures?.map((m) => m.unit_option_name) || [];
  }, [food.food_details?.measures]);



  const nutrition = useMemo(() => {
    const scaled = food.scaled_nutrition || {
      calories: food.calories,
      proteins: food.protein,
      carbs: food.carbs,
      fats: food.fats,
      fiber: food.fiber,
      sugars: food.sugar,
    };
    return formatCompactNutrition(scaled);
  }, [
    food.scaled_nutrition,
    food.protein,
    food.carbs,
    food.fats,
    food.fiber,
    food.calories,
    food.sugar,
  ]);

  const handleEditSave = (updatedFood) => {
    updateFoodItem(updatedFood);
  };

  const syncStatus = food._sync?.status; // undefined | 'syncing' | 'error' | 'deleting'

  const isRecentlyUpdated = food._updated && Date.now() - food._updated < 3000;
  const foodName =
    food.food_name ||
    food.food_details?.main_details?.food_name ||
    "Food Item";

  return (
    <React.Fragment>
    {/* Same pattern as template card (PlannedItemCard): outer div has no onClick; inner button is the only focusable and gets no ring from global CSS */}
    <div
      ref={cardRef}
      className={`food-item-card group relative bg-white rounded-xl shadow-md hover:shadow-lg transition-all duration-200 overflow-hidden ring-0 border-2 border-gray-200 ${
        isSelected
          ? "!ring-2 !ring-orange-200"
          : isRecentlyUpdated
            ? "!ring-2 !ring-green-100"
            : ""
      }`}
      style={{ width: "280px", WebkitTapHighlightColor: "transparent" }}
    >
      {/* Checkbox overlay (same as template card's action buttons: sibling of clickable, stopPropagation) */}
      {showCheckbox && (
        <div
          className="absolute top-3 left-3 z-10 w-5 h-5 rounded border-2 border-gray-300 flex items-center justify-center cursor-pointer bg-white hover:border-orange-primary transition-colors"
          aria-pressed={isSelected}
          onClick={(e) => {
            e.stopPropagation();
            onSelect?.(!isSelected);
          }}
        >
          {isSelected && (
            <Check className="w-3 h-3 text-orange-primary stroke-[3]" />
          )}
        </div>
      )}

      {/* Edit pencil — hidden during select mode and while syncing/erroring (retry button takes that corner then) */}
      {!readOnly && !showCheckbox && !syncStatus && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setShowActionsModal(true);
          }}
          className="absolute top-3 right-3 z-10 p-1.5 rounded-full bg-white border border-gray-200 text-gray-500 hover:text-orange-primary hover:border-orange-primary transition-colors"
          title="Edit"
          aria-label="Edit"
        >
          <Pencil className="w-3.5 h-3.5" />
        </button>
      )}

      {/* Background-sync status overlay */}
      {syncStatus === "syncing" && (
        <div
          className="absolute top-3 right-3 z-10 flex items-center gap-1 text-gray-400"
          title="Saving…"
        >
          <Loader2 className="w-4 h-4 animate-spin" />
        </div>
      )}
      {syncStatus === "error" && (
        <div className="absolute top-2 right-2 z-10 flex items-center gap-1">
          <AlertCircle className="w-4 h-4 text-red-500" title={food._sync?.error} />
          {/* Hidden during select mode: retrying and bulk-deleting/copying the
              same row at once would race two independent operations against it. */}
          {!showCheckbox && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                retrySync?.(food);
              }}
              className="p-1 rounded-full text-orange-primary hover:bg-orange-100"
              title="Retry"
            >
              <RefreshCw className="w-4 h-4" />
            </button>
          )}
        </div>
      )}

      <div
        role={readOnly ? undefined : "button"}
        tabIndex={readOnly ? undefined : 0}
        onMouseDown={readOnly ? undefined : (e) => e.preventDefault()}
        onClick={
          readOnly
            ? undefined
            : showCheckbox
              ? () => onSelect?.(!isSelected)
              : () => setShowActionsModal(true)
        }
        className={`block w-full min-h-full text-left rounded-xl border-0 bg-transparent p-0 focus:outline-none focus:ring-0 no-focus-ring ${
          readOnly ? "" : "hover:bg-orange-50 cursor-pointer"
        }`}
        style={{ outline: "none", boxShadow: "none", WebkitTapHighlightColor: "transparent" }}
      >
        {/* Header Section */}
        <div className="p-4 pb-3">
          <div className="flex items-start justify-between gap-2 mb-2">
            <div className="flex-1 min-w-0">
              <h3
                className="font-bold text-base text-gray-900 leading-tight line-clamp-2"
                title={foodName}
              >
                {foodName}
              </h3>
            </div>
          </div>

          {/* Quantity and Time Display */}
          <div className="flex gap-2">
            <div className="px-3 py-1.5 rounded-lg flex-shrink-0 bg-orange-50">
              <span className="text-sm font-bold text-orange-700">
                {quantity} <span className="text-xs font-normal text-orange-600">{unit}</span>
              </span>
            </div>
            <div className="px-3 py-1.5 rounded-lg flex-shrink-0 bg-gray-50">
              <span className="text-sm font-medium text-gray-700">
                {timeValue || "No time"}
              </span>
            </div>
          </div>
        </div>

        {/* Nutrition Section */}
        <div className="px-4 pb-4">
          <NutritionSummaryCard nutrition={nutrition} />
        </div>
      </div>
    </div>

      {/* Unified Actions Modal – only when not readOnly */}
      {!readOnly && (
      <FoodItemActionsModal
        isOpen={showActionsModal}
        onClose={() => setShowActionsModal(false)}
        food={food}
        onSave={handleEditSave}
        availableUnits={availableUnits}
        intakeTimeReadOnly={intakeTimeReadOnly}
      />
      )}
    </React.Fragment>
  );
};

export default React.memo(FoodItemCard);
